/**
 * Custom hook for zoom-aware responsive design
 * Replaces window.innerWidth-based responsive logic with zoom-aware alternatives
 */

import { useState, useEffect, useCallback } from 'react';
import { zoomDetection, ZoomInfo, getResponsiveBreakpoint } from '../utils/zoomDetection';

export interface UseZoomAwareResponsiveReturn {
    breakpoint: 'mobile' | 'tablet' | 'desktop';
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
    zoomInfo: ZoomInfo;
    width: number;
    height: number;
}

/**
 * Hook that provides zoom-aware responsive breakpoints
 * This replaces direct window.innerWidth usage with zoom-compensated values
 */
export const useZoomAwareResponsive = (): UseZoomAwareResponsiveReturn => {
    const [breakpoint, setBreakpoint] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
    const [zoomInfo, setZoomInfo] = useState<ZoomInfo>(zoomDetection.getZoomInfo());
    const [width, setWidth] = useState(window.innerWidth);
    const [height, setHeight] = useState(window.innerHeight);

    const updateResponsiveState = useCallback(() => {
        const newZoomInfo = zoomDetection.getZoomInfo();
        const newBreakpoint = getResponsiveBreakpoint();

        setZoomInfo(newZoomInfo);
        setBreakpoint(newBreakpoint);
        setWidth(newZoomInfo.effectiveWidth);
        setHeight(newZoomInfo.effectiveHeight);

        // Apply CSS properties for zoom-aware styling
        zoomDetection.applyCSSProperties();
    }, []);

    useEffect(() => {
        // Initial setup
        updateResponsiveState();

        // Subscribe to zoom changes
        const unsubscribe = zoomDetection.onZoomChange(updateResponsiveState);

        // Also listen to resize events as fallback
        const handleResize = () => {
            updateResponsiveState();
        };

        window.addEventListener('resize', handleResize);

        return () => {
            unsubscribe();
            window.removeEventListener('resize', handleResize);
        };
    }, [updateResponsiveState]);

    return {
        breakpoint,
        isMobile: breakpoint === 'mobile',
        isTablet: breakpoint === 'tablet',
        isDesktop: breakpoint === 'desktop',
        zoomInfo,
        width,
        height
    };
};

/**
 * Hook for zoom-aware sidebar state management
 * Replaces the existing sidebar responsive logic
 */
export const useZoomAwareSidebar = () => {
    const { breakpoint, zoomInfo } = useZoomAwareResponsive();
    const [sidebarType, setSidebarType] = useState<'mobile' | 'tablet' | 'mini-sidebar' | 'full'>('full');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    useEffect(() => {
        const effectiveWidth = zoomInfo.effectiveWidth;

        // Updated logic for proper mobile/tablet/desktop breakpoints:
        // - Mobile (< 768px): Use mobile sidebar (hidden by default, toggle with hamburger)
        // - Tablet (768px - 1024px): Use full sidebar (visible by default for iPad)
        // - Desktop (>= 1024px): Use full sidebar (visible by default)
        if (effectiveWidth < 768) {
            // Mobile: sidebar hidden by default
            setSidebarType('mobile');
        } else if (effectiveWidth >= 768 && effectiveWidth <= 1024) {
            // Tablet/iPad: sidebar visible by default
            setSidebarType('full');
        } else {
            // Desktop: sidebar visible by default
            setSidebarType('full');
        }

        // Debug log for tablet detection
        // if (effectiveWidth >= 768 && effectiveWidth <= 1024) {
        //     console.log('Tablet detected:', {
        //         effectiveWidth,
        //         sidebarType: 'full',
        //         breakpoint
        //     });
        // }
    }, [breakpoint, zoomInfo.effectiveWidth]);

    const handleSidebarToggle = useCallback((isOpen: boolean) => {
        setIsSidebarOpen(isOpen);

        // Apply classes for sidebar visibility
        const sidebar = document.querySelector('.left-sidebar, .jf-sidebar');
        const overlay = document.querySelector('.sidebar-overlay');
        const mainWrapper = document.getElementById('main-wrapper');

        if (sidebar) {
            if (isOpen) {
                sidebar.classList.add('tablet-open');
            } else {
                sidebar.classList.remove('tablet-open');
            }
        }

        if (overlay) {
            if (isOpen) {
                overlay.classList.add('active');
            } else {
                overlay.classList.remove('active');
            }
        }

        if (mainWrapper) {
            if (isOpen) {
                mainWrapper.classList.add('submenu-open');
            } else {
                mainWrapper.classList.remove('submenu-open');
            }
        }
    }, []);

    return {
        sidebarType,
        isSidebarOpen,
        handleSidebarToggle,
        breakpoint,
        zoomInfo
    };
};

/**
 * Hook for zoom-aware submenu toggle
 * Replaces the existing SubmenuToggle logic
 */
export const useZoomAwareSubmenu = () => {
    const { isDesktop, zoomInfo } = useZoomAwareResponsive();
    const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);

    const handleSubmenuToggle = useCallback((isOpen: boolean) => {
        setIsSubmenuOpen(isOpen);

        // Update main wrapper class for layout adjustments
        const mainWrapper = document.getElementById('main-wrapper');
        if (mainWrapper) {
            if (isOpen) {
                mainWrapper.classList.add('submenu-open');
            } else {
                mainWrapper.classList.remove('submenu-open');
            }
        }
    }, []);

    return {
        isDesktop,
        isSubmenuOpen,
        handleSubmenuToggle,
        zoomInfo
    };
};
